import { comparer, IObservableArray } from 'mobx';

import { withKeys } from 'bernie-core/dist/util/merge';

import { RoomModel } from 'stores/models/room-model';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

import { InCartProperty } from 'models/multi-item/multi-item-types';

import { DateUtils } from 'utils/date-utils';

import {
  Cart,
  CartPropertySearchContextInput,
  InventoryType,
  PropertyNaturalKeyInput,
  PropertyRoomInput,
  ShoppingPathType
} from '__generated__/api';

export function searchCriteriaToPropertyRoomInput(rooms: RoomModel[]): PropertyRoomInput[] {
  if (!rooms) {
    return;
  }

  return rooms.map(room => ({
    childAges: room.children && room.children.map(child => child.age),
    numberOfAdults: room.adults
  }));
}

export function adaptToPropertyNaturalKeyInputs(
  paymentPlanStartDate: string,
  paymentPlanEndDate: string,
  rooms: IObservableArray<RoomModel>,
  inventoryType: string,
  roomTypeCode: string,
  ratePlanCode: string,
  id: string,
  isAttach: boolean,
  roomIndex: number = null,
  isMixMatchEnabled?: boolean
): PropertyNaturalKeyInput[] {
  if (!paymentPlanStartDate || !paymentPlanEndDate || paymentPlanStartDate.split(',').length !== ratePlanCode.split(',').length) {
    return;
  }

  return ratePlanCode
    .split(',')
    .filter(singleRatePlan => singleRatePlan)
    .map((singleRatePlan, idx) =>
      adaptToPropertyNaturalKeyInput(
        paymentPlanStartDate.split(',')[idx],
        paymentPlanEndDate.split(',')[idx],
        rooms,
        inventoryType,
        roomTypeCode,
        singleRatePlan,
        id,
        isAttach,
        roomIndex,
        isMixMatchEnabled
      )
    );
}

function adaptToPropertyNaturalKeyInput(
  paymentPlanStartDate: string,
  paymentPlanEndDate: string,
  rooms: IObservableArray<RoomModel>,
  inventoryType: string,
  roomTypeCode: string,
  ratePlanCode: string,
  id: string,
  isAttach: boolean,
  roomIndex: number,
  isMixMatchEnabled?: boolean
): PropertyNaturalKeyInput {
  return {
    checkIn: DateUtils.fromDateToBexApiDate(new Date(paymentPlanStartDate)),
    checkOut: DateUtils.fromDateToBexApiDate(new Date(paymentPlanEndDate)),
    id,
    inventoryType: adaptInventoryType(inventoryType),
    ratePlanId: ratePlanCode,
    roomTypeId: roomTypeCode,
    rooms: searchCriteriaToPropertyRoomInput(isMixMatchEnabled && roomIndex && roomIndex >= 0 ? [rooms[roomIndex]] : rooms),
    shoppingPath: isAttach ? ShoppingPathType.ATTACH : ShoppingPathType.STANDALONE
  };
}

export function adaptToCartPropertySearchContextInput(
  searchCriteria: SearchCriteriaModel,
  regionId: string
): CartPropertySearchContextInput {
  return {
    checkIn: DateUtils.fromISODateToBexApiDate(searchCriteria.startDate),
    checkOut: DateUtils.fromISODateToBexApiDate(searchCriteria.endDate),
    rooms: toPropertyRoomInputs(searchCriteria),
    regionId
  };
}

function toPropertyRoomInputs(searchCriteria: SearchCriteriaModel): PropertyRoomInput[] {
  if (!searchCriteria.rooms || searchCriteria.rooms.length === 0) {
    return;
  }

  return searchCriteria.rooms.map(room => ({
    childAges: (room.children && room.children.map(child => child.age)) || [],
    numberOfAdults: room.adults
  }));
}

export function cartItemToInCartProperty(isMixMatchEnabled) {
  return (item: Cart.Items, index: number, allItems: Cart.Items[]): InCartProperty => {
    const { id, summary, offers, gallery } = item;
    const unit = offers[0].offer && offers[0].offer.units && offers[0].offer.units[0];
    const marketingChannelId = offers[0].offer && offers[0].offer.marketingChannelId;
    const ratePlanPriceDetails =
      unit && unit.ratePlans && unit.ratePlans[0] && unit.ratePlans[0].priceDetails && unit.ratePlans[0].priceDetails[0];
    const leadPrice = ratePlanPriceDetails && ratePlanPriceDetails.price && ratePlanPriceDetails.price.lead;
    const totalPrice = ratePlanPriceDetails && ratePlanPriceDetails.price && ratePlanPriceDetails.price.total;
    const priceWithLoyaltyPoints =
      ratePlanPriceDetails &&
      ratePlanPriceDetails.priceAfterLoyaltyPointsApplied &&
      ratePlanPriceDetails.priceAfterLoyaltyPointsApplied.lead;
    const fees =
      ratePlanPriceDetails &&
      ratePlanPriceDetails.fees &&
      ratePlanPriceDetails.fees.filter(fee => fee && fee.description).map(fee => fee.description);
    const minRoomsLeft = ratePlanPriceDetails && ratePlanPriceDetails.availability && ratePlanPriceDetails.availability.minRoomsLeft;
    const available =
      ratePlanPriceDetails &&
      ratePlanPriceDetails.availability &&
      ratePlanPriceDetails.availability.available &&
      (minRoomsLeft === null || minRoomsLeft > numberOfDuplicates(allItems.slice(0, index), item));

    const naturalKeys = offers[0].naturalKeys;
    const firstNaturalKey = naturalKeys[0];
    const lastNaturalKey = naturalKeys[naturalKeys.length - 1];

    const roomImage = isMixMatchEnabled
      ? offers[0].offer &&
        offers[0].offer.units &&
        offers[0].offer.units[0] &&
        offers[0].offer.units[0].photos &&
        offers[0].offer.units[0].photos[0] &&
        offers[0].offer.units[0].photos[0].url
      : gallery && gallery.featured && gallery.featured.url;

    return {
      id,
      marketingChannelId,
      name: summary && summary.name,
      checkin: firstNaturalKey && firstNaturalKey.checkIn && firstNaturalKey.checkIn.isoFormat,
      checkout: lastNaturalKey && lastNaturalKey.checkOut && lastNaturalKey.checkOut.isoFormat,
      image: roomImage,
      city: (summary && summary.location && summary.location.address && summary.location.address.city) || '',
      adults: numberOfAdults(firstNaturalKey.rooms),
      children: numberOfChildren(firstNaturalKey.rooms),
      rooms: firstNaturalKey.rooms && firstNaturalKey.rooms.length,
      ratePlan: {
        roomName: unit && unit.name,
        available,
        leadPrice: {
          currencySymbol: leadPrice && leadPrice.currency && leadPrice.currency.symbol,
          formatted: leadPrice && leadPrice.formatted,
          raw: leadPrice && leadPrice.raw
        },
        totalPrice: {
          currencySymbol: totalPrice && totalPrice.currency && totalPrice.currency.symbol,
          formatted: totalPrice && totalPrice.formatted,
          raw: totalPrice && totalPrice.raw
        },
        priceAfterLoyaltyPointsApplied: {
          currencySymbol: priceWithLoyaltyPoints && priceWithLoyaltyPoints.currency && priceWithLoyaltyPoints.currency.symbol,
          formatted: priceWithLoyaltyPoints && priceWithLoyaltyPoints.formatted,
          raw: priceWithLoyaltyPoints && priceWithLoyaltyPoints.raw
        },
        fees
      }
    };
  };
}

function numberOfDuplicates(items: Cart.Items[], item: Cart.Items): number {
  return items
    .filter(otherItem => {
      const compareKeys = ['id', 'roomTypeId', 'ratePlanId', 'checkIn', 'checkOut'];

      return comparer.structural(
        withKeys(compareKeys).merge({}, item.offers[0].naturalKeys[0]),
        withKeys(compareKeys).merge({}, otherItem.offers[0].naturalKeys[0])
      );
    })
    .map(i => i.offers[0].naturalKeys[0].rooms.length)
    .reduce((acc, roomsCount) => acc + roomsCount, 0);
}

export function numberOfAdults(rooms: Cart.Rooms[]): number {
  if (!rooms || rooms.length === 0) {
    return 0;
  }

  return rooms.reduce((acc, room) => acc + room.numberOfAdults, 0);
}

export function numberOfChildren(rooms: Cart.Rooms[]): number {
  if (!rooms || rooms.length === 0) {
    return 0;
  }

  return rooms.reduce((acc, room) => {
    const roomChildren = room.childAges ? room.childAges.length : 0;

    return acc + roomChildren;
  }, 0);
}

/*
        MERCHANT(1, 24),
        DIRECT_AGENCY(5, 24),
        JUMBOTOURS(9, 81),
        HOTEL_BEDS(10, 82),
        DESPEGAR(11, 84),
        THOMASCOOK_UK(12, 85), **
        TOURICO(13, 86),
        THOMASCOOK_EUR(20, 93), **
        JALAN(26,99),
        HOME_AWAY(28, 83),
        SUNHOTELS(29,104); **
        */
export function adaptInventoryType(inventoryType: string): InventoryType {
  switch (inventoryType) {
    case '1':
      return InventoryType.MERCHANT;
    case '5':
      return InventoryType.DIRECT_AGENCY;
    case '9':
      return InventoryType.JUMBOTOURS;
    case '10':
      return InventoryType.HOTEL_BEDS;
    case '11':
      return InventoryType.DESPEGAR;
    case '13':
      return InventoryType.TOURICO;
    case '26':
      return InventoryType.JALAN;
    case '28':
      return InventoryType.HOME_AWAY;
    default:
      return InventoryType.MERCHANT;
  }
}
